import React from 'react';
import { Box, Typography } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import RunDatesGrid from './RunDatesGrid';
import PortalDetailPanel from './PortalDetailPanel';
import { clickLinkFromPortal } from '../../../utils/adPortal';

const MEDIA_TYPE_LABELS = { 1: 'Image', 2: 'Video', 3: 'Audio', 4: '3D' };

// a changed ad portal is not compared field by field here (the comparison view does that): the whole block is marked
const PORTAL_DIFF_KEYS = new Set(['localizedPairs', 'correspondingMedia', 'portalLinks']);
const NO_DIFF_KEYS = new Set();

export default function AdDetailPanel({ data, diffKeys = new Set(), side }) {
  if (!data) return null;

  const mediaLinks   = Array.isArray(data.mediaLinks)   ? data.mediaLinks   : [];
  const scheduleDates = Array.isArray(data.scheduleDates) ? data.scheduleDates : [];
  // A 2D ad's portal is just its click link: one row, not the 3D portal block
  const clickLink    = !data.is3d && data.portal ? clickLinkFromPortal(data.portal) : '';

  return (
    <Box>
      {data.localizedName != null && (
        <>
          <SectionBlock title="Identity" />
          <LocalizedField
            label="Name"
            fieldKey="localizedName"
            data={data.localizedName}
            diffKeys={diffKeys}
            side={side}
          />
        </>
      )}

      <SectionBlock title="Ad Details" />
      <FieldRow
        label="Placement ID"
        value={data.placementId != null ? String(data.placementId) : null}
        isChanged={diffKeys.has('placementId')}
        side={side}
      />
      <FieldRow
        label="Placement Key"
        value={data.placementKey ?? null}
        isChanged={diffKeys.has('placementKey')}
        side={side}
      />
      <FieldRow
        label="Duration (sec)"
        value={data.durationSeconds != null ? String(data.durationSeconds) : null}
        isChanged={diffKeys.has('durationSeconds')}
        side={side}
      />
      <FieldRow
        label="Placement Type"
        value={data.is3d == null ? null : (data.is3d ? '3D' : '2D')}
        isChanged={diffKeys.has('is3d')}
        side={side}
      />
      <FieldRow
        label="Media Type"
        value={data.mediaTypeId != null ? (MEDIA_TYPE_LABELS[data.mediaTypeId] ?? String(data.mediaTypeId)) : null}
        isChanged={diffKeys.has('mediaTypeId')}
        side={side}
      />

      {(data.is3d || data.model) && (
        <FieldRow
          label="3D Model"
          value={data.model ? `${data.model.displayName ?? data.model.modelKey} (${data.model.modelKey})` : null}
          isChanged={diffKeys.has('model')}
          side={side}
        />
      )}

      {mediaLinks.length > 0 && (
        <>
          <SectionBlock title="Media" />
          {mediaLinks.map((entry, i) => (
            <FieldRow
              key={entry.localeId ?? i}
              label={`Media URL (${(entry.localeId ?? '').slice(0, 2).toUpperCase() || '?'})`}
              value={entry.mediaLink ?? '—'}
              isChanged={diffKeys.has('mediaLinks')}
              side={side}
            />
          ))}
        </>
      )}

      {clickLink && (
        <FieldRow
          label="Click Link"
          value={clickLink}
          isChanged={diffKeys.has('portal')}
          side={side}
        />
      )}

      {data.portal && data.is3d && (
        <PortalDetailPanel
          embedded
          data={data.portal}
          diffKeys={diffKeys.has('portal') ? PORTAL_DIFF_KEYS : NO_DIFF_KEYS}
          side={side}
        />
      )}

      {scheduleDates.length > 0 && (
        <>
          <SectionBlock title="Schedule" />
          {/* Label above, not beside: the calendars are tall, so a side label column would just sit empty */}
          <Box sx={{ px: 2, py: 1, bgcolor: diffKeys.has('scheduleDates') ? '#fffbeb' : 'transparent', borderBottom: '1px solid #f1f5f9' }}>
            <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', fontWeight: 600, fontSize: 11, mb: 0.75 }}>
              Run Dates
            </Typography>
            <RunDatesGrid dates={scheduleDates} />
          </Box>
        </>
      )}
    </Box>
  );
}
